import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, Megaphone, Eye, MousePointerClick, Trophy, TrendingUp } from "lucide-react";
import AdminLayout from "@/components/app/AdminLayout";
import api from "@/lib/api";

const KPIS = [
  { key: "total_leads", label: "סך לידים", icon: Users, color: "text-brand-amber" },
  { key: "total_campaigns", label: "קמפיינים פעילים", icon: Megaphone, color: "text-brand-charcoal" },
  { key: "total_views", label: "צפיות דף נחיתה", icon: Eye, color: "text-emerald-600" },
  { key: "total_affiliate_clicks", label: "קליקים לאפיליאייט", icon: MousePointerClick, color: "text-blue-600" },
];

export default function AdminDashboard() {
  const [summary, setSummary] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [leads, setLeads] = useState([]);
  const [leaderboard, setLeaderboard] = useState({ days: 30, rows: [] });

  useEffect(() => {
    (async () => {
      try {
        const [s, c, l, lb] = await Promise.all([
          api.get("/analytics/summary"),
          api.get("/campaigns"),
          api.get("/leads?limit=5"),
          api.get("/affiliates/leaderboard?days=30&limit=5"),
        ]);
        setSummary(s.data);
        setCampaigns(c.data);
        setLeads(l.data);
        setLeaderboard(lb.data);
      } catch (err) {
        console.error("Dashboard load failed:", err);
      }
    })();
  }, []);

  return (
    <AdminLayout title="סקירה כללית">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="kpi-cards">
        {KPIS.map((k) => {
          const Icon = k.icon;
          return (
            <div key={k.key} className="bg-white rounded-2xl p-5 border border-stone-200">
              <div className="flex items-center justify-between mb-3">
                <Icon className={`w-5 h-5 ${k.color}`} />
                <span className="text-xs text-stone-500">{k.label}</span>
              </div>
              <div className="text-3xl font-[800]" data-testid={`kpi-${k.key}`}>
                {summary ? summary[k.key]?.toLocaleString() ?? 0 : "—"}
              </div>
            </div>
          );
        })}
      </div>

      {/* Affiliate Leaderboard */}
      <div className="bg-white rounded-2xl p-6 border border-stone-200 mb-6" data-testid="affiliate-leaderboard">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-brand-amber" />
            <h2 className="font-[800]">טבלת ביצועי אפיליאייט</h2>
            <span className="text-xs text-stone-500">· {leaderboard.days} ימים אחרונים</span>
          </div>
          <Link to="/admin/affiliates" className="text-sm font-bold text-brand-amber">ניהול אפיליאייטים →</Link>
        </div>
        {leaderboard.rows.length === 0 ? (
          <p className="text-sm text-stone-500 py-8 text-center">אין נתוני ביצועים עדיין.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-right text-xs text-stone-500 border-b border-stone-200">
                  <th className="py-2 font-bold">#</th>
                  <th className="py-2 font-bold">אפיליאייט</th>
                  <th className="py-2 font-bold">List ID</th>
                  <th className="py-2 font-bold">לידים</th>
                  <th className="py-2 font-bold">קליקים</th>
                  <th className="py-2 font-bold">CTR</th>
                  <th className="py-2 font-bold">סטטוס</th>
                </tr>
              </thead>
              <tbody>
                {leaderboard.rows.map((r, idx) => (
                  <tr key={r.id} className="border-b border-stone-100 last:border-0" data-testid={`leaderboard-row-${idx}`}>
                    <td className="py-3 font-[800] text-brand-amber">{idx + 1}</td>
                    <td className="py-3 font-bold">{r.name}</td>
                    <td className="py-3 text-stone-500 text-xs">{r.list_id || "—"}</td>
                    <td className="py-3 font-[800]">{r.leads.toLocaleString()}</td>
                    <td className="py-3">{r.clicks.toLocaleString()}</td>
                    <td className="py-3">
                      <span className="inline-flex items-center gap-1">
                        <TrendingUp className="w-3.5 h-3.5 text-emerald-600" />
                        {r.click_through_rate}%
                      </span>
                    </td>
                    <td className="py-3">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${r.is_active ? "bg-emerald-100 text-emerald-700" : "bg-stone-100 text-stone-600"}`}>
                        {r.is_active ? "פעיל" : "כבוי"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Recent leads */}
        <div className="bg-white rounded-2xl p-6 border border-stone-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-[800]">לידים אחרונים</h2>
            <Link to="/admin/leads" className="text-sm font-bold text-brand-amber">לכל הלידים →</Link>
          </div>
          {leads.length === 0 ? (
            <p className="text-sm text-stone-500 py-8 text-center">אין לידים עדיין.</p>
          ) : (
            <div className="space-y-2">
              {leads.map((l) => (
                <div key={l.id} className="flex items-center justify-between py-2 border-b border-stone-100 last:border-0 text-sm">
                  <div className="font-bold">{l.name}</div>
                  <div className="text-stone-500 truncate">{l.email}</div>
                  <div className="text-xs text-stone-400">{new Date(l.created_at).toLocaleDateString("he-IL")}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Campaigns */}
        <div className="bg-white rounded-2xl p-6 border border-stone-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-[800]">קמפיינים</h2>
            <Link to="/admin/campaigns" className="text-sm font-bold text-brand-amber">ניהול →</Link>
          </div>
          {campaigns.length === 0 ? (
            <p className="text-sm text-stone-500 py-8 text-center">אין קמפיינים.</p>
          ) : (
            <div className="space-y-2">
              {campaigns.slice(0, 5).map((c) => (
                <div key={c.id} className="flex items-center justify-between py-2 border-b border-stone-100 last:border-0 text-sm">
                  <div>
                    <div className="font-bold">{c.brand_name}</div>
                    <div className="text-xs text-stone-500">/{c.slug} · list: {c.list_id}</div>
                  </div>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${c.is_active ? "bg-emerald-100 text-emerald-700" : "bg-stone-100 text-stone-600"}`}>
                    {c.is_active ? "פעיל" : "כבוי"}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
